import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { uniq } from 'lodash-es';
import { combineLatest, filter, Observable, of } from 'rxjs';

import { ElementsState, runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementEvent,
  CanvasElementFrom,
  ColumnId,
  getFormGroupLikeWrapper,
} from '@blaze-runtime/engine-domain';
import { WizardElementMetaDataFeatures } from '@blaze-shared/elements-domain';

import {
  describeGroupedFormErrors,
  FormStateService,
  FormValidationReportService,
} from '../../../services/forms/runtime/public-api';
import {
  findParentFormGroupLike,
  isElementChildOfAtSlot,
} from '../../../utils/layout/relationship';
import {
  isElementType,
  isFormListLike,
  isVariantOfFormContainer,
  isWizard,
} from '../../element-model/element-type-guards';
import { getWrapperRefs } from '../../element-model/wrapper-refs';
import { ButtonFormGroupState } from '../../models/render-element.models';
import { ComponentValidationHandler } from '../models/validation-handler.models';

/**
 * Guards output emission for elements that target form-group-like containers
 * (e.g. a button inside a form-group, or a wizard submit/next action).
 *
 * Extracted from the output pipeline to keep ElementOutputPipeline focused on
 * the core output-subscription pipeline.
 */
@Injectable({ providedIn: 'root' })
export class FormGroupOutputSynchronizer {
  private readonly formStateService = inject(FormStateService);
  private readonly formValidationReport = inject(FormValidationReportService);
  private readonly store = inject(Store<ElementsState>);

  /**
   * Runs form-group-like output checks.
   * @returns `true` if the output should proceed, `false` to block emission.
   */
  checkFormGroupLikeTargets(
    event: CanvasElementEvent,
    element: CanvasElement,
    validationHandler: ComponentValidationHandler | undefined,
    elements: Dictionary<CanvasElement>,
    wizardMeta: WizardElementMetaDataFeatures | undefined,
  ): boolean {
    const buttonFormGroupData = validationHandler?.getValidationData(
      element.id,
    )?.buttonFormGroupData;

    const formGroupId = buttonFormGroupData?.formGroupElementId;
    const parentFormGroup = this.findFormGroupLikeTarget(element, event, elements, formGroupId);
    if (
      parentFormGroup &&
      !this.handleFormGroupLikeSubmit(element, event, elements, wizardMeta, buttonFormGroupData)
    ) {
      return false;
    }
    return true;
  }

  /**
   * Returns an observable that resolves once all forms inside a form-group-like
   * container are initialized.  Used as a `delayWhen` guard in the output stream.
   */
  waitForFormGroupsInitialized(
    element: CanvasElement | undefined,
    event: CanvasElementEvent,
    elements: Dictionary<CanvasElement>,
    buttonFormGroupData: ButtonFormGroupState | undefined,
  ): Observable<any> {
    const formGroupLike =
      element &&
      this.findFormGroupLikeTarget(
        element,
        event,
        elements,
        buttonFormGroupData?.formGroupElementId,
      );
    if (!formGroupLike) {
      return of(undefined);
    }
    const refs = getWrapperRefs(formGroupLike);
    const isLinkAction = formGroupLike.props.content.action === 'link';
    const skipFormIds = isLinkAction
      ? getFormGroupLikeWrapper(refs)
          .filter(({ keyColumnId }) => !keyColumnId)
          .map(({ formId }) => formId)
      : [];
    const formIds = getFormGroupLikeWrapper(refs)
      .map(({ formId }) => formId)
      .filter(
        (formId) =>
          !skipFormIds.includes(formId) &&
          (isVariantOfFormContainer(elements[formId]) || isFormListLike(elements[formId])),
      );

    if (formIds.length === 0) {
      return of([]);
    }
    return combineLatest(
      formIds.map((formId) =>
        this.store.select(runtimeSelectors.selectFormStateById(formId)).pipe(filter(Boolean)),
      ),
    );
  }

  // ─── private helpers ──────────────────────────────────────────────────────

  private findFormGroupLikeTarget(
    element: CanvasElement,
    event: CanvasElementEvent,
    elements: Dictionary<CanvasElement>,
    formGroupElementId: string | undefined,
  ): CanvasElementFrom<'formGroup' | 'wizard'> | undefined {
    if (!formGroupElementId) {
      return undefined;
    }
    if (element.type === 'button' && event.eventType === 'click') {
      return findParentFormGroupLike(elements, element.id);
    }
    if (isWizard(element) && (event.eventType === 'submit' || event.eventType === 'next')) {
      return element;
    }
    return undefined;
  }

  /**
   * Validates form state and dispatches required side-effects before allowing
   * a form-group-like output event to proceed.
   * @returns `true` if the output should proceed.
   */
  private handleFormGroupLikeSubmit(
    element: CanvasElement,
    event: CanvasElementEvent,
    elements: Dictionary<CanvasElement>,
    wizardMeta: WizardElementMetaDataFeatures | undefined,
    buttonFormGroupData: ButtonFormGroupState | undefined,
  ): boolean {
    if (!buttonFormGroupData) {
      throw new Error('assertion error - buttonFormGroupData should be defined');
    }

    const slotId =
      (wizardMeta?.selectedSlotId as ColumnId | undefined) ?? (element?.slots?.ids[0] as ColumnId);
    const filledSteps = wizardMeta?.filledSteps as ColumnId[] | undefined;
    const wizardSteps = isElementType(element, 'wizard')
      ? (element.props.content.steps?.items ?? [])
      : [];

    let stepRequired = true;
    let currentStepFilledForms: string[] = [];
    if (isElementType(element, 'wizard') && slotId !== undefined) {
      const slotIndex = element.slots?.ids.indexOf(slotId);
      const step = wizardSteps[slotIndex ?? -1];
      if (step) {
        stepRequired = !!step.required;
      }
      currentStepFilledForms = buttonFormGroupData.allFormsState
        .filter(
          ({ formElementId, isFormBlank }) =>
            !isFormBlank && isElementChildOfAtSlot(elements, formElementId, element.id, slotId),
        )
        .map(({ formElementId }) => formElementId);
    }

    // On submit, always include the current step's forms — even when blank/untouched.
    // Otherwise required fields (incl. via form field conditions) on the last step are
    // skipped when the user clicks Submit without interacting (BL-10628).
    const formsState = buttonFormGroupData.allFormsState.filter(({ formElementId }) =>
      this.shouldValidateFormOnEvent({
        formElementId,
        element,
        eventType: event.eventType,
        elements,
        slotId,
        filledSteps,
      }),
    );

    const performValidation =
      event.eventType === 'submit' ||
      stepRequired ||
      !formsState.every(({ isFormBlank }) => isFormBlank);

    if (performValidation && !formsState.every(({ isFormValid }) => isFormValid)) {
      formsState.forEach(({ formElementId }) => {
        this.formStateService.setSubmitted(formElementId);
      });
      this.formValidationReport.showErrors(
        describeGroupedFormErrors(
          isElementType(element, 'wizard') ? element : undefined,
          formsState.map(({ formElementId, messages }) => ({
            formId: formElementId,
            notifications: messages,
          })),
          elements,
        ),
      );
      return false;
    }

    if (formsState.length > 0 && formsState.every(({ isFormPosted }) => isFormPosted)) {
      // form post in progress; an empty forms list must not match - every() returns true
      // on [], which would silently block the event with no error whenever the validation
      // data is briefly out of date (e.g. right after forms were regenerated)
      return false;
    }

    if (event.eventType === 'submit') {
      formsState.forEach(({ formElementId }) => {
        this.formStateService.setPosted(formElementId);
      });
    }
    if (element.type === 'wizard') {
      this.store.dispatch(
        runtimeActions.updateElementMeta({
          elementId: element.id,
          meta: {
            dataFeatures: {
              elementType: 'wizard',
              ...(event.data !== undefined
                ? { selectedSlotId: event.eventType === 'next' ? event.data.slotId : event.data }
                : {}),
              filledSteps:
                currentStepFilledForms.length > 0
                  ? uniq([...(filledSteps ?? []), slotId])
                  : (filledSteps ?? []).filter((step) => step !== slotId),
            },
          },
        }),
      );
    }
    return true;
  }

  private shouldValidateFormOnEvent({
    formElementId,
    element,
    eventType,
    elements,
    slotId,
    filledSteps,
  }: {
    formElementId: string;
    element: CanvasElement;
    eventType: CanvasElementEvent['eventType'];
    elements: Dictionary<CanvasElement>;
    slotId: ColumnId;
    filledSteps: ColumnId[] | undefined;
  }): boolean {
    if (!isElementType(element, 'wizard')) {
      return true;
    }

    const isOnCurrentStep = isElementChildOfAtSlot(elements, formElementId, element.id, slotId);

    if (eventType === 'next') {
      return isOnCurrentStep;
    }

    if (isOnCurrentStep) {
      return true;
    }

    const isOnRequiredStep = element.props.content.steps?.items.some(
      (step, stepIndex) =>
        step.required &&
        isElementChildOfAtSlot(elements, formElementId, element.id, element.slots.ids[stepIndex]),
    );
    if (isOnRequiredStep) {
      return true;
    }

    const isOnFilledStep = !!filledSteps?.some((filledSlotId) =>
      isElementChildOfAtSlot(elements, formElementId, element.id, filledSlotId),
    );
    return isOnFilledStep;
  }
}
